'use client'

import * as React from 'react'
import { SlidersHorizontal, BedDouble, Banknote, MapPin, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore } from '@/lib/store'
import { FilterModal } from './filter-modal'
import { useI18n } from '@/lib/i18n'

// Mobile filter pills — rendered INLINE at the start of the /search
// active-chips row (one merged row, user-directed 2026-08-02; the earlier
// standalone row duplicated the chips: "2 pokoje" appeared as a pill AND as a
// removable chip). Below lg the pills replace the rooms/price/location chips
// (those get `hidden lg:inline-flex` in page.tsx): an ACTIVE pill shows the
// value + an × that clears it, body tap opens the FilterModal pre-scrolled
// via initialSection. Desktop is untouched — the `contents lg:hidden`
// wrapper removes the pills at lg and the full chips row returns.
// Apply contract: mutations dispatch `header-search-triggered {source:
// 'filters'}` — the page listener runs handleSearch('filters').

function triggerSearch() {
  window.dispatchEvent(
    new CustomEvent('header-search-triggered', { detail: { source: 'filters' } })
  )
}

// Compact CZK for pill value labels — "25k" not "25 000 Kč" (pill width).
function kCZK(n: number): string {
  return n >= 1000 && n % 1000 === 0 ? `${n / 1000}k` : String(n)
}

export function MobileQuickFilters() {
  const { t } = useI18n()
  const {
    filters,
    activeTags,
    updateFilter,
    selectedCity,
    setSelectedCity,
    selectedDistricts,
    setSelectedDistricts,
  } = useSearchStore()

  const [modalOpen, setModalOpen] = React.useState(false)
  const [initialSection, setInitialSection] = React.useState<string | undefined>(undefined)

  const openAt = (section?: string) => {
    setInitialSection(section)
    setModalOpen(true)
  }

  // Same active-count arithmetic as FiltersButton — the badge must agree with
  // the header's.
  const locationCount =
    (filters.type && filters.type !== 'apartment' ? 1 : 0) +
    (filters.transaction_type && filters.transaction_type !== 'rent' ? 1 : 0) +
    (selectedCity && selectedCity !== 'Praha' ? 1 : 0) +
    (selectedDistricts.length > 0 ? 1 : 0)
  const numericCount =
    (filters.rooms_min ? 1 : 0) + (filters.rooms_max ? 1 : 0) +
    (filters.area_min ? 1 : 0) + (filters.area_max ? 1 : 0) +
    (filters.price_min ? 1 : 0) + (filters.price_max ? 1 : 0)
  const priceModeCount = filters.use_total_price === false ? 1 : 0
  const totalActiveCount = activeTags.length + numericCount + locationCount + priceModeCount

  // Value labels — an active pill shows its state, not its name (it REPLACES
  // the removable chip below lg).
  const roomsLabel = (() => {
    const { rooms_min: lo, rooms_max: hi } = filters
    if (!lo && !hi) return null
    if (lo && hi) return lo === hi ? `${lo}` : `${lo}–${hi}`
    return lo ? `${lo}+` : `≤ ${hi}`
  })()
  const priceLabel = (() => {
    const { price_min: lo, price_max: hi } = filters
    if (!lo && !hi) return null
    if (lo && hi) return `${kCZK(lo)}–${kCZK(hi)}`
    return hi ? `≤ ${kCZK(hi)}` : `${kCZK(lo!)}+`
  })()
  // Districts when picked, else the city — this pill fully replaces the
  // chips row's location chip on mobile (which shows exactly city-or-districts).
  const districtLabel =
    selectedDistricts.length > 0
      ? selectedDistricts[0] + (selectedDistricts.length > 1 ? ` +${selectedDistricts.length - 1}` : '')
      : selectedCity || null

  const clearRooms = () => {
    updateFilter('rooms_min', undefined)
    updateFilter('rooms_max', undefined)
    triggerSearch()
  }
  const clearPrice = () => {
    updateFilter('price_min', undefined)
    updateFilter('price_max', undefined)
    triggerSearch()
  }
  const clearLocation = () => {
    // Mirrors the chips row's removeLocation (city cleared too).
    setSelectedCity('')
    setSelectedDistricts([])
    triggerSearch()
  }

  const pillBase =
    'inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-[13px] font-semibold transition-transform active:scale-[0.97]'
  const pillIdle = 'border-outline bg-surface text-foreground'
  const pillActive = 'border-primary/30 bg-primary/10 text-primary'

  // Active pill: body opens the modal at its section, the × clears. The × is
  // a span-with-role inside the button (nested buttons are invalid HTML) —
  // same recipe as the recent-search badges in MobileSearchSheet.
  const clearX = (onClear: () => void, what: string) => (
    <span
      role="button"
      tabIndex={-1}
      aria-label={`${t('search.removeFilter', 'Remove')} ${what}`}
      onClick={(e) => {
        e.stopPropagation()
        onClear()
      }}
      className="-mr-1 rounded-full p-0.5 opacity-60 hover:opacity-100 hover:bg-black/10 transition-opacity"
    >
      <X className="h-3 w-3" />
    </span>
  )

  return (
    <>
      <div className="contents lg:hidden">
        <button type="button" onClick={() => openAt(undefined)} className={cn(pillBase, pillIdle)}>
          <SlidersHorizontal className="h-4 w-4 text-ink-secondary" />
          {t('search.filters', 'Filters')}
          {totalActiveCount > 0 && (
            <span className="font-mono inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-primary px-1 text-[10.5px] font-bold text-white">
              {totalActiveCount}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => openAt('rooms')}
          className={cn(pillBase, roomsLabel ? pillActive : pillIdle)}
        >
          <BedDouble className="h-4 w-4" />
          {roomsLabel
            ? `${roomsLabel} ${t('search.quickRoomsUnit', 'rooms')}`
            : t('search.quickRooms', 'Rooms')}
          {roomsLabel && clearX(clearRooms, t('search.quickRooms', 'Rooms'))}
        </button>

        <button
          type="button"
          onClick={() => openAt('price')}
          className={cn(pillBase, priceLabel ? pillActive : pillIdle)}
        >
          <Banknote className="h-4 w-4" />
          {priceLabel ?? t('search.quickPrice', 'Price')}
          {priceLabel && clearX(clearPrice, t('search.quickPrice', 'Price'))}
        </button>

        <button
          type="button"
          onClick={() => openAt('location')}
          className={cn(pillBase, districtLabel ? pillActive : pillIdle)}
        >
          <MapPin className="h-4 w-4" />
          {districtLabel ?? t('search.quickDistrict', 'District')}
          {districtLabel && clearX(clearLocation, t('search.quickDistrict', 'District'))}
        </button>
      </div>

      <FilterModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onApply={triggerSearch}
        initialSection={initialSection}
      />
    </>
  )
}
